(function () {
  if (customElements.get('wss-product-slider')) return;

  class WssProductSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-na-track]');
      if (!this.track) return;

      this.prevButtons = this.querySelectorAll('[data-na-prev]');
      this.nextButtons = this.querySelectorAll('[data-na-next]');
      this.progressBars = this.querySelectorAll('[data-na-progress]');

      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onClick = this._onClick.bind(this);

      this.prevButtons.forEach((b) => b.addEventListener('click', () => this._scrollByPage(-1)));
      this.nextButtons.forEach((b) => b.addEventListener('click', () => this._scrollByPage(1)));
      this.track.addEventListener('scroll', this._onScroll, { passive: true });
      this.addEventListener('click', this._onClick);
      window.addEventListener('resize', this._onResize);

      this._initGalleries();
      this._initDrag();
      this._update();
      this._positionArrows();
    }

    _initDrag() {
      const track = this.track;
      let pointerId = null;
      let startX = 0;
      let startScroll = 0;
      let moved = false;

      track.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        pointerId = e.pointerId;
        startX = e.clientX;
        startScroll = track.scrollLeft;
        moved = false;
      });

      track.addEventListener('pointermove', (e) => {
        if (pointerId === null || e.pointerId !== pointerId) return;
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) > 5) {
          moved = true;
          track.setPointerCapture(pointerId);
          track.classList.add('is-grabbing');
          track.style.scrollSnapType = 'none';
          track.style.scrollBehavior = 'auto';
        }
        if (moved) {
          e.preventDefault();
          track.scrollLeft = startScroll - dx;
        }
      });

      const endDrag = (e) => {
        if (pointerId === null || (e && e.pointerId !== pointerId)) return;
        pointerId = null;
        if (!moved) return;
        track.classList.remove('is-grabbing');
        track.style.scrollBehavior = '';
        const step = this._slideStep();
        const target = Math.max(0, Math.round(track.scrollLeft / step) * step);
        track.scrollTo({ left: target, behavior: 'smooth' });
        clearTimeout(this._snapTimer);
        this._snapTimer = setTimeout(() => {
          track.style.scrollSnapType = '';
        }, 500);
        this._suppressClick = true;
        setTimeout(() => {
          this._suppressClick = false;
        }, 0);
      };

      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);
      track.addEventListener('dragstart', (e) => {
        if (pointerId !== null || this._suppressClick) e.preventDefault();
      });

      this.addEventListener(
        'click',
        (e) => {
          if (this._suppressClick) {
            e.preventDefault();
            e.stopPropagation();
          }
        },
        true
      );
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
    }

    _slideStep() {
      const slide = this.track.querySelector('[data-na-slide]');
      if (!slide) return this.track.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap || '0') || 0;
      return slide.offsetWidth + gap;
    }

    _scrollByPage(direction) {
      const step = this._slideStep();
      const perView = Math.max(1, Math.floor(this.track.clientWidth / step));
      this.track.scrollBy({ left: direction * step * perView, behavior: 'smooth' });
    }

    _onScroll() {
      if (this._raf) return;
      this._raf = requestAnimationFrame(() => {
        this._raf = null;
        this._update();
      });
    }

    _onResize() {
      this._update();
      this._positionArrows();
    }

    _update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const left = this.track.scrollLeft;
      const atStart = left <= 1;
      const atEnd = left >= max - 1;

      this.prevButtons.forEach((b) => {
        b.toggleAttribute('disabled', atStart);
        b.setAttribute('aria-disabled', atStart);
      });
      this.nextButtons.forEach((b) => {
        b.toggleAttribute('disabled', atEnd);
        b.setAttribute('aria-disabled', atEnd);
      });

      const ratio = max > 0 ? left / max : 0;
      const visible = this.track.clientWidth / this.track.scrollWidth;
      this.progressBars.forEach((bar) => {
        const thumb = bar.querySelector('[data-na-progress-thumb]');
        if (!thumb) return;
        const widthPct = Math.min(100, Math.max(10, visible * 100));
        thumb.style.width = widthPct + '%';
        thumb.style.left = ratio * (100 - widthPct) + '%';
      });
    }

    _positionArrows() {
      const media = this.track.querySelector('[data-na-media]');
      if (!media) return;
      this.style.setProperty('--wss-na-arrow-top', media.offsetHeight / 2 + 'px');
    }

    _initGalleries() {
      this.querySelectorAll('[data-na-gallery]').forEach((gallery) => {
        const media = gallery.closest('[data-na-media]');
        if (!media) return;

        let raf = null;
        gallery.addEventListener(
          'scroll',
          () => {
            if (raf) return;
            raf = requestAnimationFrame(() => {
              raf = null;
              const dots = media.querySelectorAll('[data-na-dot]');
              if (!dots.length) return;
              const index = Math.round(gallery.scrollLeft / gallery.clientWidth);
              dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
            });
          },
          { passive: true }
        );
      });
    }

    _rebuildGallery(card, gallery, images) {
      const oldImg = gallery.querySelector('img');
      const sizes = oldImg ? oldImg.getAttribute('sizes') : null;
      const alt = oldImg ? oldImg.getAttribute('alt') : '';

      gallery.innerHTML = '';
      images.forEach((img, i) => {
        const item = document.createElement('div');
        item.className = 'wss-na__gallery-item';
        const el = document.createElement('img');
        if (i === 0) el.setAttribute('data-na-main-image', '');
        el.src = img.src;
        if (img.srcset) el.setAttribute('srcset', img.srcset);
        if (sizes) el.setAttribute('sizes', sizes);
        el.alt = alt || '';
        if (img.w) el.width = img.w;
        if (img.h) el.height = img.h;
        el.loading = 'lazy';
        item.appendChild(el);
        gallery.appendChild(item);
      });
      gallery.scrollTo({ left: 0, behavior: 'auto' });

      const dotsWrap = card.querySelector('[data-na-dots]');
      if (dotsWrap) {
        dotsWrap.innerHTML = '';
        if (images.length > 1) {
          images.forEach((_, i) => {
            const dot = document.createElement('span');
            dot.className = 'wss-na__dot' + (i === 0 ? ' is-active' : '');
            dot.setAttribute('data-na-dot', '');
            dot.setAttribute('data-index', i);
            dotsWrap.appendChild(dot);
          });
          dotsWrap.style.display = '';
        } else {
          dotsWrap.style.display = 'none';
        }
      }
    }

    _onClick(event) {
      const cardArrow = event.target.closest('[data-na-card-arrow]');
      if (cardArrow && this.contains(cardArrow)) {
        event.preventDefault();
        event.stopPropagation();
        const media = cardArrow.closest('[data-na-media]');
        const gallery = media && media.querySelector('[data-na-gallery]');
        if (gallery) {
          const dir = cardArrow.dataset.dir === 'prev' ? -1 : 1;
          gallery.scrollBy({ left: dir * gallery.clientWidth, behavior: 'smooth' });
        }
        return;
      }

      const dot = event.target.closest('[data-na-dot]');
      if (dot && this.contains(dot)) {
        event.preventDefault();
        event.stopPropagation();
        const media = dot.closest('[data-na-media]');
        const gallery = media && media.querySelector('[data-na-gallery]');
        if (gallery) {
          const index = parseInt(dot.dataset.index, 10) || 0;
          gallery.scrollTo({ left: index * gallery.clientWidth, behavior: 'smooth' });
        }
        return;
      }

      const swatch = event.target.closest('[data-na-swatch]');
      if (!swatch || !this.contains(swatch)) return;
      event.preventDefault();

      const card = swatch.closest('[data-na-card]');
      if (!card) return;

      const gallery = card.querySelector('[data-na-gallery]');

      let images = null;
      if (swatch.dataset.images) {
        try {
          images = JSON.parse(swatch.dataset.images);
        } catch (e) {
          images = null;
        }
      }

      if (gallery && images && images.length) {
        this._rebuildGallery(card, gallery, images);
      } else {
        const image = card.querySelector('[data-na-main-image]');
        if (image && swatch.dataset.image) {
          if (swatch.dataset.srcset) {
            image.setAttribute('srcset', swatch.dataset.srcset);
          } else {
            image.removeAttribute('srcset');
          }
          image.setAttribute('src', swatch.dataset.image);

          if (gallery) {
            gallery.scrollTo({ left: 0, behavior: 'auto' });
            const dots = card.querySelectorAll('[data-na-dot]');
            dots.forEach((dot, i) => dot.classList.toggle('is-active', i === 0));
          }
        }
      }

      if (swatch.dataset.url) {
        card.querySelectorAll('[data-na-link]').forEach((link) => {
          link.setAttribute('href', swatch.dataset.url);
        });
      }

      card.querySelectorAll('[data-na-swatch]').forEach((s) => {
        s.classList.toggle('is-active', s === swatch);
        s.setAttribute('aria-pressed', s === swatch);
      });
    }
  }

  customElements.define('wss-product-slider', WssProductSlider);
})();